// Recursive-descent evaluator for `@if` condition expressions.
// Supports `&&`, `||`, comparison operators, and one-level+ parentheses.
import type { LuisArrayVariables } from '../../types'
import type { LuisObjectVariables } from '../../types'
import { preprocessConditionExpression } from './if-condition-preprocess'
import { tokenizeConditionExpression, type ConditionToken } from './if-condition-tokens'
import { compareConditionValues, isTruthyConditionValue, resolveConditionValue } from './if-condition-values'

// Evaluate one condition string and return boolean result.
export function evaluateIfCondition(
  condition: string,
  scope: Record<string, string>,
  variables: Record<string, string>,
  objectVariables: LuisObjectVariables,
  arrays: LuisArrayVariables,
): boolean {
  const preprocessed = preprocessConditionExpression(condition, scope, variables, objectVariables, arrays)
  const tokens = tokenizeConditionExpression(preprocessed)
  // Return false when tokenizer cannot parse condition expression.
  if (!tokens) return false
  const state = { _index: 0, _tokens: tokens, _scope: scope, _variables: variables, _objectVariables: objectVariables, _arrays: arrays }
  const result = parseLogicalOr(state)
  // Return false when token stream is not fully consumed.
  return result !== null && state._index === state._tokens.length ? result : false
}

// Internal parser cursor state used only inside one condition evaluation pass.
type ParseState = {
  _index: number
  _tokens: ConditionToken[]
  _scope: Record<string, string>
  _variables: Record<string, string>
  _objectVariables: LuisObjectVariables
  _arrays: LuisArrayVariables
}

// Parse `||` expressions with left-to-right associativity.
function parseLogicalOr(state: ParseState): boolean | null {
  let left = parseLogicalAnd(state)
  // Stop parsing when left side cannot be evaluated.
  if (left === null) return null

  while (matchOperator(state, '||')) {
    const right = parseLogicalAnd(state)
    // Reject expression when right side is invalid.
    if (right === null) return null
    left = left || right
  }

  // Return parsed `||` expression result.
  return left
}

// Parse `&&` expressions with higher precedence than `||`.
function parseLogicalAnd(state: ParseState): boolean | null {
  let left = parseBooleanPrimary(state)
  // Stop parsing when left side cannot be evaluated.
  if (left === null) return null

  while (matchOperator(state, '&&')) {
    const right = parseBooleanPrimary(state)
    // Reject expression when right side is invalid.
    if (right === null) return null
    left = left && right
  }

  // Return parsed `&&` expression result.
  return left
}

// Parse one boolean primary: parenthesized expression or comparison/value.
function parseBooleanPrimary(state: ParseState): boolean | null {
  const token = state._tokens[state._index]
  // Parse parenthesized expression recursively.
  if (token?.type === 'lparen') {
    state._index += 1
    const nested = parseLogicalOr(state)
    // Reject expression when nested parse fails or `)` is missing.
    if (nested === null || state._tokens[state._index]?.type !== 'rparen') return null
    state._index += 1
    return nested
  }

  const leftToken = consumeValueToken(state)
  // Reject when parser cannot read left operand token.
  if (!leftToken) return null

  const operatorToken = state._tokens[state._index]
  // Parse comparison when next token is comparison operator.
  if (operatorToken?.type === 'operator' && isComparisonOperator(operatorToken.value)) {
    state._index += 1
    const rightToken = consumeValueToken(state)
    // Reject comparisons when right operand is missing.
    if (!rightToken) return null
    const left = resolveConditionValue(leftToken, state._scope, state._variables, state._objectVariables, state._arrays)
    const right = resolveConditionValue(rightToken, state._scope, state._variables, state._objectVariables, state._arrays)
    return compareConditionValues(left, operatorToken.value, right)
  }

  // Evaluate standalone value tokens as truthy/falsey.
  const value = resolveConditionValue(leftToken, state._scope, state._variables, state._objectVariables, state._arrays)
  return isTruthyConditionValue(value)
}

// Consume one value token and advance parser index.
function consumeValueToken(state: ParseState): string | null {
  const token = state._tokens[state._index]
  // Reject non-value token positions.
  if (!token || token.type !== 'value') return null
  state._index += 1
  return token.value
}

// Match one operator token and advance index on success.
function matchOperator(state: ParseState, operator: '&&' | '||'): boolean {
  const token = state._tokens[state._index]
  // Return false when current token is not requested operator.
  if (!token || token.type !== 'operator' || token.value !== operator) return false
  state._index += 1
  return true
}

// Check whether token is a comparison operator.
function isComparisonOperator(operator: ConditionToken['value']): operator is '===' | '!==' | '>=' | '<=' | '>' | '<' {
  return operator === '===' || operator === '!==' || operator === '>=' || operator === '<=' || operator === '>' || operator === '<'
}
